import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImageGalleryModal } from "./ImageGalleryModal";
import { WalkthroughModal } from "./WalkthroughModal";
import { X } from "lucide-react";

interface ModelImages {
  "2D": string[];
  "3D": string[];
  exterior: string[];
  interior: string[];
}

interface Model {
  id: number;
  name: string;
  squareFootage: number;
  beds: number;
  baths: number;
  widgetUrl: string;
  images: ModelImages;
}

interface ModelBoxProps {
  model: Model;
}

export const ModelBox = ({ model }: ModelBoxProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  // List of exclusive model names that should show coming soon
  const exclusiveModels = ["Gilmour", "Di Meola", "Satriani", "Knopfler", "Moore"];
  const isExclusiveModel = exclusiveModels.includes(model.name);

  const handleOptionClick = (option: string) => {
    if (isExclusiveModel) {
      setShowComingSoon(true);
      setIsHovered(false);
    } else {
      setActiveModal(option);
      setIsHovered(false);
    }
  };

  const openCalendlyModal = () => {
    setShowComingSoon(false);
    setShowCalendly(true);
  };

  const closeCalendlyModal = () => setShowCalendly(false);
  const closeComingSoonModal = () => setShowComingSoon(false);

  // Use first image as background
  const backgroundImage = model.images.exterior[0] || model.images["2D"][0];

  const hoverOptions = [
    { label: "Walk In", key: "walkthrough" }
  ];

  return (
    <>
      <motion.div
        className="relative w-full h-48 md:h-64 overflow-hidden cursor-pointer group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.2 }}
      >
        {/* Background Image with Loading States */}
        <div className="absolute inset-0">
          {/* Placeholder while loading */}
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 animate-pulse">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#E4683B] border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
          )}
          
          {/* Error state */}
          {imageError && (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
              <div className="text-white text-center">
                <div className="text-2xl mb-2">🏠</div>
                <div className="text-sm opacity-75">Image unavailable</div>
              </div>
            </div>
          )}
          
          {/* Actual image */}
          <img
            src={backgroundImage}
            alt={`${model.name} exterior`}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 group-hover:scale-110 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            loading="lazy"
            decoding="async"
          />
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Default Content */}
        <AnimatePresence>
          {!isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col justify-between p-4 text-white"
            >
              <h3 className="text-lg md:text-xl font-bold text-center">
                {model.name}
              </h3>
              
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold mb-2">
                  {model.squareFootage} SF
                </p>
              </div>
              
              <p className="text-sm md:text-base text-center">
                {model.beds === 0 ? 'Studio' : `${model.beds} Bed${model.beds > 1 ? 's' : ''}`} - {model.baths} Bath{model.baths > 1 ? 's' : ''}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hover Options */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 flex flex-col justify-center items-center p-4 bg-black/60 backdrop-blur-sm"
            >
              <h3 className="text-white text-lg font-bold mb-4 text-center">
                {model.name}
              </h3>
              
              <div className="space-y-2 w-full max-w-xs">
                {hoverOptions.map((option) => (
                  <motion.button
                    key={option.key}
                    onClick={() => handleOptionClick(option.key)}
                    className="w-full px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors duration-200 text-sm md:text-base"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {option.label}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Modals */}
      <ImageGalleryModal
        isOpen={activeModal === "2D" || activeModal === "3D"}
        onClose={() => setActiveModal(null)}
        images={activeModal ? model.images[activeModal as keyof ModelImages] : []}
        title={`${model.name} - ${activeModal?.toUpperCase()}`}
      />

      <WalkthroughModal
        isOpen={activeModal === "walkthrough"}
        onClose={() => setActiveModal(null)}
        widgetUrl={model.widgetUrl}
        modelName={model.name}
      />

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full relative">
            <button 
              onClick={closeComingSoonModal}
              className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-5 w-5" />
            </button>
            
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-4">Walk-through is coming soon</h3>
              <button
                onClick={openCalendlyModal}
                className="bg-[#E4683B] hover:bg-[#d55a32] text-white px-6 py-2 rounded-lg transition-colors"
              >
                Any urgent questions?
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh] relative">
            <button 
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
    </>
  );
};